import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { api } from '../api/client';
import { useTheme } from '../context/ThemeContext';
import fieldiqIcon from '../assets/fieldiq-icon.png';
import fieldiqIconLight from '../assets/fieldiq-icon-light.png';
import fieldiqWordmark from '../assets/fieldiq-wordmark.png';
import fieldiqWordmarkLight from '../assets/fieldiq-wordmark-light.png';

export default function ForgotPasswordScreen() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { theme } = useTheme();
  const icon = theme === 'light' ? fieldiqIconLight : fieldiqIcon;
  const wordmark = theme === 'light' ? fieldiqWordmarkLight : fieldiqWordmark;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoading(true);
    try {
      await api.auth.forgotPassword(email.trim());
    } catch {
      // Always show success — don't leak whether email exists
    } finally {
      setIsLoading(false);
      setSent(true);
    }
  };

  return (
    <div className="min-h-full bg-fieldiq-dark flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={icon} alt="FieldIQ" className="w-20 h-20 rounded-2xl mx-auto mb-4" />
          <img src={wordmark} alt="FieldIQ" className="h-8 mx-auto mb-2" />
          <p className="text-fieldiq-text">Reset Your Password</p>
        </div>

        {sent ? (
          <div className="text-center">
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-6 mb-6">
              <Mail className="w-12 h-12 text-fieldiq-accent mx-auto mb-3" />
              <h2 className="text-lg font-bold text-white mb-2">Check Your Email</h2>
              <p className="text-fieldiq-text text-sm">
                If an account exists for <span className="text-white">{email}</span>, we've sent a password reset link.
              </p>
              <p className="text-fieldiq-muted text-xs mt-3">
                The link expires in 1 hour. Check your spam folder if you don't see it.
              </p>
            </div>
            <Link
              to="/login"
              className="text-fieldiq-accent text-sm hover:underline inline-flex items-center gap-1"
            >
              <ArrowLeft size={22} />
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-4 mb-6">
              <p className="text-sm text-fieldiq-text">
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-fieldiq-text mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-fieldiq-card border border-fieldiq-border rounded-lg px-4 py-3 text-white focus:border-fieldiq-accent focus:outline-none"
                  placeholder="you@company.com"
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-fieldiq-accent text-fieldiq-dark font-bold py-3 rounded-lg hover:bg-fieldiq-accent/90 transition disabled:opacity-50"
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-fieldiq-accent text-sm hover:underline inline-flex items-center gap-1"
              >
                <ArrowLeft size={22} />
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
